'use client';
// TODO: Fix React 19 compiler lint errors. See https://react.dev/learn/react-compiler
/* eslint-disable react-hooks/set-state-in-effect */

import { useClientConfig } from '@providers/ClientConfigProvider';
import cn from '@utils/cn';
import { useCallback, useEffect, useRef, useState } from 'react';
import PlayIcon from './PlayIcon';
import TemplateImageClient from './TemplateImageClient';
import { DESIGN_SIZE } from './constants';

type DesignAnimatedProps = {
  hash: string;
  name?: string;
  isAnimated?: boolean;
  hideIcon?: boolean;
  isPlaying?: boolean;
  designSize?: DESIGN_SIZE;
};

enum PLAY_STATE {
  NONE,
  LOADING,
  PLAYING,
  ERROR,
}

export default function DesignAnimated({
  hash,
  name,
  isAnimated = false,
  hideIcon = false,
  isPlaying = false,
  designSize = DESIGN_SIZE.DEFAULT,
}: DesignAnimatedProps) {
  const config = useClientConfig();
  const [playState, setPlayState] = useState(PLAY_STATE.NONE);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const onCanPlayThrough = useCallback(() => {
    setPlayState(PLAY_STATE.PLAYING);
  }, []);

  const onError = useCallback(() => {
    setPlayState(PLAY_STATE.ERROR);
  }, []);

  const onLoad = useCallback(() => {
    setIsImageLoaded(true);
  }, []);

  useEffect(() => {
    if (!isAnimated || playState === PLAY_STATE.ERROR) {
      return;
    }

    if (isPlaying && playState === PLAY_STATE.NONE) {
      setPlayState(PLAY_STATE.LOADING);
    }

    if (isPlaying && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }
  }, [isAnimated, isPlaying, playState]);

  const hasVideo = isAnimated && (playState === PLAY_STATE.LOADING || playState === PLAY_STATE.PLAYING);
  const hideImage = playState === PLAY_STATE.PLAYING && isPlaying;

  const videoSrc = `${config.CDN_URL}/designs/${hash}/videos/template.mp4`;

  return (
    <div className="relative inline-block">
      <TemplateImageClient hash={hash} name={name} isHidden={hideImage} designSize={designSize} onLoad={onLoad} />
      {hasVideo && (
        <video
          ref={videoRef}
          loop
          muted
          autoPlay
          src={videoSrc}
          preload="auto"
          className={cn(
            'pointer-events-none absolute left-0 top-0 z-10 h-full w-full object-contain',
            'transition-opacity duration-500',
            {
              'opacity-0': !isPlaying,
            },
          )}
          onCanPlayThrough={onCanPlayThrough}
          playsInline
          onError={onError}
        />
      )}
      {!hideIcon && isAnimated && isImageLoaded && playState !== PLAY_STATE.ERROR && <PlayIcon />}
    </div>
  );
}
